import type { CampaignAlternateScriptDto, SaveAlternateScriptRequest } from './campaign'
import type {
  SurveyResponseSubmission, TagAssignmentSubmission, PromptResponseSubmission,
} from './canvasser'

// CF-1 follow-up foundation DTOs (consumed by P2-P5 — names are contract).
export type FollowUpKind = 'SignDropOff' | 'CallBack' | 'Chase' | 'Custom'
export type FollowUpMode = 'Route' | 'Call'
export type FollowUpStatus = 'Open' | 'Done' | 'Couldnt' | 'Cancelled' | 'Superseded' | 'Declined'
export type CampaignDisplayMode = 'Standard' | 'FinalPush'

export const FOLLOW_UP_KIND_LABELS: Record<Exclude<FollowUpKind, 'Custom'>, string> = {
  SignDropOff: 'Sign drop-off',
  CallBack: 'Call-back',
  Chase: 'Chase to vote',
}

export interface FollowUpKindDto {
  id: string
  name: string
  mode: FollowUpMode
  isActive: boolean
}

export interface SaveFollowUpKindRequest {
  name: string
  mode: FollowUpMode
}

export interface RestoreStarterQuestionsResult {
  created: number
}

export interface FollowUpKindCountDto {
  kind: FollowUpKind
  customKindId: string | null
  label: string
  mode: FollowUpMode
  open: number
  done: number
  couldnt: number
  cancelled: number
  superseded: number
  declined: number
}

export interface FollowUpSummaryDto {
  kinds: FollowUpKindCountDto[]
}

export interface FollowUpItemsParams {
  kind?: FollowUpKind
  customKindId?: string
  status?: FollowUpStatus
  page?: number
  pageSize?: number
}

export interface FollowUpItemRowDto {
  id: string
  contactId: string
  displayName: string
  address: string | null
  phone: string | null
  kind: FollowUpKind
  customKindId: string | null
  status: FollowUpStatus
  note: string | null
  createdAt: string
  /** [CF-1 P4] Contact.IsDoNotContact: the organizer's Call button is withheld. */
  doNotContact: boolean
}

export interface FollowUpItemsPageDto {
  items: FollowUpItemRowDto[]
  total: number
  page: number
  pageSize: number
}

export interface UpdateFollowUpItemRequest {
  status: FollowUpStatus
  note?: string | null
}

export interface CampaignViewSettingsDto {
  displayMode: CampaignDisplayMode
  finalPushFrom: string | null // yyyy-MM-dd
  effectiveMode: CampaignDisplayMode
}

export interface SaveCampaignViewSettingsRequest {
  displayMode: CampaignDisplayMode
  finalPushFrom: string | null
}

// ---- CF-1 P2: follow-up routes (mirrors backend FollowUpRouteDTOs.cs) ----

export interface CutFollowUpRoutesRequest {
  kind: FollowUpKind
  customKindId?: string | null
  /** null = every cuttable (Open, unrouted) item of this kind, resolved server-side at enqueue. */
  itemIds: string[] | null
  driverCount: number
}

export interface RecutFollowUpBatchRequest {
  driverCount: number
}

export interface FollowUpRoutesParams {
  kind: FollowUpKind
  customKindId?: string | null
}

export interface FollowUpRouteAssignmentDto {
  assignmentId: string
  grantId: string | null
  scheduledDate: string // yyyy-MM-dd (0001-01-01 = static)
}

export interface FollowUpRouteDto {
  turfId: string
  label: string
  turfIndex: number
  stopCount: number
  openItemCount: number
  closedItemCount: number
  estimatedMinutes: number | null
  isLocked: boolean
  assignments: FollowUpRouteAssignmentDto[]
}

export interface FollowUpRouteBatchDto {
  turfMapId: string
  label: string
  kind: FollowUpKind
  customKindId: string | null
  createdAt: string
  routes: FollowUpRouteDto[]
}

export interface FollowUpRoutesDto {
  unroutedOpenCount: number
  batches: FollowUpRouteBatchDto[]
}

/** The FollowUpRouteCut job's `result`. */
export interface FollowUpRouteCutResultDto {
  turfMapId: string | null
  routeCount: number
  routedItemCount: number
  skippedItemCount: number
}

// ---- CF-1 P4: call lists (mirrors backend FollowUpCall DTOs) ----

export type CallOutcome =
  | 'Talked' | 'NoAnswer' | 'LeftMessage' | 'Busy' | 'CallBackLater' | 'Refused' | 'WrongNumber' | 'Deceased'

/** Labels match the server's CallOutcomeSemantics.Label. */
export const CALL_OUTCOME_LABELS: Record<CallOutcome, string> = {
  Talked: 'Talked',
  NoAnswer: 'No answer',
  LeftMessage: 'Left a message',
  Busy: 'Busy',
  CallBackLater: 'Call back later',
  Refused: 'Refused',
  WrongNumber: 'Wrong number',
  Deceased: 'Deceased',
}

export interface FollowUpCallBatchDto {
  id: string
  kind: FollowUpKind
  customKindId: string | null
  label: string
  assignedGrantId: string | null
  assignedCanvasserName: string | null
  scheduledDate: string | null // yyyy-MM-dd
  total: number
  called: number
  open: number
  createdAt: string
}

export interface CallBatchItemRowDto {
  itemId: string
  contactId: string
  displayName: string
  phone: string | null
  doNotContact: boolean
  reason: string
  note: string | null
  status: FollowUpStatus
  calledAt: string | null
  lastOutcome: CallOutcome | null
  attemptCount: number
  scriptId: string | null
}

export interface FollowUpCallBatchDetailDto {
  batch: FollowUpCallBatchDto
  items: CallBatchItemRowDto[]
}

export interface FollowUpCallBatchesPageDto {
  items: FollowUpCallBatchDto[]
  total: number
  page: number
  pageSize: number
}

export interface CallBatchesParams {
  kind?: FollowUpKind
  customKindId?: string
  page?: number
  pageSize?: number
}

export interface CreateCallBatchRequest {
  kind: FollowUpKind
  customKindId?: string | null
  itemIds: string[]
  assignedGrantId?: string | null
  scheduledDate?: string | null
  label?: string
}

export interface CreateCallBatchResult {
  batch: FollowUpCallBatchDto
  included: number
  excluded: {
    doNotContact: number
    phoneRestricted: number
    noPhone: number
    notOpen: number
    alreadyQueued: number
  }
}

export interface AssignCallBatchRequest {
  assignedGrantId: string
  scheduledDate: string | null
}

export interface FollowUpScriptConfigDto {
  kind: FollowUpKind
  customKindId: string | null
  defaultScriptId: string | null
  defaultScriptLabel: string | null
  alternates: CampaignAlternateScriptDto[]
}

export interface SaveFollowUpScriptRequest {
  kind: FollowUpKind
  customKindId?: string | null
  defaultScriptId: string | null
  alternates: SaveAlternateScriptRequest[]
}

export interface RecordCallSubmission {
  clientCallId: string
  itemId: string
  outcome: CallOutcome
  scriptId?: string | null
  clientTimestamp?: string
  surveyResponses?: SurveyResponseSubmission[] | null
  promptResponses?: PromptResponseSubmission[] | null
  tagAssignments?: TagAssignmentSubmission[] | null
  note?: string
  markDoNotContact?: boolean
  /** [CF-1 P5] The batch the call was made from; the server pins the call to that batch's row. Set at send time. */
  callBatchId?: string
}

export interface RecordCallResult {
  clientCallId: string
  itemId: string
  success: boolean
  rejected: boolean
  error: string | null
  itemStatus: FollowUpStatus | null
}

// ── CF-1 P5: chase to vote (api/campaigns/{id}/follow-up/chase/*) ──

/** Matches Voter.Models.ElectionType (serialized via JsonStringEnumConverter). */
export type ElectionType =
  | 'Unknown' | 'General' | 'Primary' | 'Special' | 'Municipal' | 'Runoff' | 'Recall' | 'Referendum'

/** The campaign's chase settings. Thresholds are the organizer's own numbers, never a contact's score. */
export interface ChaseSettingsDto {
  electionDate: string | null
  electionType: ElectionType | null
  ideologyMin: number | null
  ideologyMax: number | null
  propensityMin: number | null
  activismMin: number | null
  recontactDays: number
}

/** PUT body: replaces the chase columns only. Election date and type are both set or both null. */
export type SaveChaseSettingsRequest = ChaseSettingsDto

/** Counts only. alreadyVoted is null when no target election is set; widenedCount only when asked
 *  (and an election is set) — when widenedCapped it is the cap. */
export interface ChaseSummaryDto {
  electionSet: boolean
  pledgedOrSupporter: number
  alreadyVoted: number | null
  leftToChase: number
  votedDataAsOf: string | null
  widenedCount: number | null
  widenedCapped: boolean
  computedAt: string
}

export interface PrepareChaseRequest {
  mode: FollowUpMode
  includeWidened: boolean
}

/** The ChasePrepare job's `result`. */
export interface ChasePrepareResultDto {
  batchId: string
  itemCount: number
  newItemsCreated: number
  excludedVoted: number
  excludedRecentlyContacted: number
  widenedIncluded: number
}

export interface CutChaseBatchRequest {
  driverCount: number
}

export interface ChaseCallListResultDto {
  callBatchId: string
}
